import React, { useEffect } from 'react';
import { CssType, ThemeType } from 'src/theming/jssTypes';
import lodash from 'lodash';
import withStyles, { WithStylesProps } from 'react-jss';
import { Collapse, Modal, Progress, Tooltip, message } from 'src/design-system/components/atoms';
import { CheckCircleFilled, CloseOutlined, ExclamationCircleFilled, CloseCircleFilled, CloudDownloadOutlined } from '@ant-design/icons';
import { createAndDownloadCSVFileFromJSON, promiseAllWithConcurrency } from 'src/utils/utils';
import { useTranslation } from 'react-i18next';
const { Panel } = Collapse;

const styles = (theme: ThemeType): CssType => {
    return {
    wrapper: {
        position: 'fixed',
        left: theme.spacing[6],
        bottom: theme.spacing[0],
        zIndex: 10000,
    },
    collapsible: {
        width: '400px',
        '& .ant-collapse-content-box': {
            padding: theme.spacing[0],
            backgroundColor: theme.color.semantic.background.neutral.secondary,
        },
        '& .ant-collapse-header': {
            alignItems: 'center !important',
            padding: `${theme.spacing[3]} ${theme.spacing[4]} !important`,
            backgroundColor: `${theme.color.semantic.background.neutral.inverse} !important`,
        },
        '& .ant-collapse-arrow': {
            color: `${theme.color.semantic.background.neutral.secondary} !important`,
            fontSize: `${theme.typography.fontSize.md} !important`,
            marginRight: `${theme.spacing[5]} !important`,
        },
    },
    panel: {
        backgroundColor: theme.color.semantic.background.neutral.inverse,
        alignItems: 'center',
    },
    header: {
        backgroundColor: theme.color.semantic.background.neutral.inverse,
        color: theme.color.semantic.background.neutral.secondary,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    closeIcon: {
        color: theme.color.semantic.background.neutral.secondary,
    },
    progressData: {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        fontSize: theme.typography.fontSize.xs,
        color: theme.color.semantic.text.tertiary,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
    },
    progressMetrics: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    metricTile: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '133px',
        paddingTop: theme.spacing[6],
        paddingBottom: theme.spacing[6],
    },
    metricNumber: {
        fontSize: theme.typography.fontSize.md,
        color: theme.color.semantic.text.primary,
    },
    metricStatus: {
        fontSize: theme.typography.fontSize.sm,
        color: theme.color.semantic.text.tertiary,
    },
    };
}

interface GenericUploadStatusBarProps extends WithStylesProps<typeof styles> {
    heading: string;
    apiCallFn: (requestData: any) => Promise<any>;
    data: any[];
    getRequestBody: (batchData: any[]) => any;
    onCompletion: () => any;
    failureArrayKeysToShow: string[];
    isSingleApi?: boolean;
    onAllBatchesComplete?: () => Promise<any>;
    dataHeaders: any[];
    requestMetadata?: Record<string, any>;
    concurrencyLimit?: number;
    batchSize?: number;
    jobCompleteMessage?: string;
    zIndexToUse?: number;
    theme: ThemeType;
}

const GenericUploadStatusBar: React.FC<GenericUploadStatusBarProps> = (props) => {
    const { t } = useTranslation();
    const {
        classes,
        theme,
        heading,
        data,
        getRequestBody,
        apiCallFn,
        onCompletion,
        failureArrayKeysToShow,
        dataHeaders = [],
        requestMetadata = {},
        concurrencyLimit = 3,
        batchSize: batchSizeValue  = 25,
        jobCompleteMessage = 'The operation is complete. You may download the failed results if any by clicking the download icon. To see your changes, refresh the data.',
        zIndexToUse = 1000
    } = props;
    const [pendingCount, setPendingCount] = React.useState(data.length);
    const [successCount, setSuccessCount] = React.useState(0);
    const [failures, setFailures] = React.useState<any[]>([]);
    const [failureCount, setFailureCount] = React.useState(0);
    const progressPercentage = pendingCount === 0 ? 100 : Math.floor((successCount + failureCount) / (data.length) * 100);
    const jobComplete = pendingCount === 0;

    useEffect(() => {
        const batchSize = props.isSingleApi ? 1 : batchSizeValue;
        const batchedData = lodash.chunk(data, batchSize);
        const promisesFns = batchedData.map((batch, index) => {
            return (async () => {
                const dataForRequestBody = props.isSingleApi ? batch[0] : [...dataHeaders, ...batch];
                const requestBody = getRequestBody(dataForRequestBody);
                const response = await apiCallFn({
                    ...requestBody,
                    ...requestMetadata,
                    bulkUploadData: {
                        batchNumber: index + 1,
                        totalBatches: batchedData.length,
                    },
                });
                let success;
                let failuresData;
                let failure;
                if (response.isSuccess) {
                    failuresData = lodash.get(response, 'data.failures', []);

                    failuresData = failuresData.map((failureItem: any) => {
                        if (typeof failureItem.rowNum === 'number' && !isNaN(failureItem.rowNum)) {
                            const globalRowNum = (index * batchSize) + failureItem.rowNum;
                            return {
                                ...failureItem,
                                rowNum: globalRowNum
                            };
                        }
                        return failureItem;
                    });

                    const successes = lodash.get(response, 'data.successes');
                    success = successes !== null && successes !== undefined ? successes : batch.length - failuresData.length;
                    failure = batch.length - success;
                } else {
                    failuresData = lodash.get(response, 'data.failures', batch.map((data, dataIdx) => {
                        const toRet = {
                            ...data,
                            rowNum: (index * batchSize) + dataIdx + 1,
                            message: response.errorMessage || 'Something went wrong',
                            reason: response.errorMessage || 'Something went wrong',
                        };

                        return toRet;
                    }));
                    success = 0;
                    failure = batch.length;
                }
                setSuccessCount(prevCount => prevCount + success);
                setFailures(prevFailures => prevFailures.concat(failuresData));
                setFailureCount(prevCount => prevCount + failure);
                setPendingCount(prevCount => prevCount - batch.length);
            });
        });

        promiseAllWithConcurrency(promisesFns, concurrencyLimit)
            .then(() => {
                if (props.onAllBatchesComplete) {
                    props.onAllBatchesComplete();
                }
            })
            .catch(error => {
                console.error('Error processing batches:', error);
            });
    }, []);

    useEffect(() => {
        if (jobComplete) {
            message.success(t(jobCompleteMessage));
        }
        return () => {};
    }, [jobComplete]);

    const renderHeader = () => {
        return <div className={classes.header}>
            <Progress type="circle" percent={progressPercentage} showInfo={false} width={24} strokeWidth={14} />
            <span style={{ marginLeft: theme.spacing[3] }}>{t(heading)}</span>
        </div>;
    };

    const renderMetric = (status: string, icon: any, count: number) => {
        return <div className={classes.metricTile}>
            <div className={classes.metricNumber}>{count}</div>
            <div className={classes.metricStatus}>
                {icon}
                <span style={{ marginLeft: theme.spacing[1] }}>{t(status)}</span>
            </div>
        </div>;
    };

    const downloadFailures = () => {
        if (!jobComplete) return;
        createAndDownloadCSVFileFromJSON(failures, 'failure-data-' + new Date().toISOString(), failureArrayKeysToShow);
    };

    const renderContent = () => {
        return <div>
            <div className={classes.progressData}>
                <span>{progressPercentage}% {t('Completed')}</span>
                <Tooltip title={t('Download failed results')}>
                    <CloudDownloadOutlined onClick={downloadFailures} style={{
                        fontSize: theme.typography.fontSize.md,
                        color: jobComplete ? theme.color.semantic.text.tertiary : theme.color.semantic.background.neutral.subtle,
                        cursor: jobComplete ? 'pointer' : 'not-allowed',
                    }}/>
                </Tooltip>
            </div>
            <div className={classes.progressMetrics}>
                {renderMetric('Pending', <ExclamationCircleFilled style={{
                    color: theme.color.semantic.accent.orange.strong,
                }} /> , pendingCount)}
                {renderMetric('Successful', <CheckCircleFilled style={{
                    color: theme.color.semantic.accent.green.strong,
                }} /> , successCount)}
                {renderMetric('Failed', <CloseCircleFilled style={{
                    color: theme.color.semantic.brand.danger.primary,
                }} /> , failureCount)}
            </div>
        </div>;
    };

    return (
        <div className={classes.wrapper} style = {{ zIndex: zIndexToUse }}>
            <Collapse
                defaultActiveKey={['1']}
                expandIconPosition="right"
                className={classes.collapsible}
            >
                <Panel
                    className={classes.panel}
                    header={renderHeader()}
                    key="1"
                    extra={<CloseOutlined className={classes.closeIcon} onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (jobComplete) {
                            onCompletion();
                        } else {
                            Modal.confirm({
                                title: t('Attention'),
                                content: t('If you move away, the process will be incomplete. Are you sure you want to proceed ?'),
                                okText: t('Proceed'),
                                cancelText: t('Dont Proceed'),
                                width: 500,
                                onOk: () => onCompletion(),
                                okType: 'danger',
                            });
                        }
                    }}/>}
                    showArrow={true}
                >
                    {renderContent()}
                </Panel>
            </Collapse>
        </div>
    );
};

export default withStyles(styles, { injectTheme: true })(GenericUploadStatusBar);
